import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { StripeCardElementOptions, StripeElementsOptions } from '@stripe/stripe-js';
import { StripeCardNumberComponent, StripeService } from 'ngx-stripe';

import { fromEvent, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { CommonProvider, ReportProvider, UserProvider } from 'src/app/providers';
import { User } from 'src/app/model';
import { CurrencyPipe } from 'src/app/pipes/currency.pipe';
import {CommonService, LoaderService, ToasterService} from 'src/app/services';
import { ReportState } from 'src/app/model/Enums';
import { PricingConstant, PricingPlanType } from 'src/app/constants/pricing.constants';

@Component({
  selector: 'app-checkout',
  templateUrl: './checkout.component.html',
  styleUrls: ['./checkout.component.scss']
})
export class CheckoutComponent implements OnInit, OnDestroy, AfterViewInit {

  @ViewChild(StripeCardNumberComponent) card: StripeCardNumberComponent;
  @ViewChild('checkoutForm') checkoutForm: NgForm;

  private subscribeParams: Subscription;
  user: User;
  isPayClicked: boolean = false;
  pricingPlanType: string = '';
  reportId: string = '';

  promoCodeAllowed: boolean = false;
  isPromoApplied: boolean = false;
  promoCode: string = '';

  payableAmount: number = null;

  basePrice: number = PricingConstant.PRICE_PER_REPORT;
  constPricingPlanType = PricingPlanType;
  showMore: boolean = false;

  checkoutDetails = {
    CARD_NAME: '',
    SAME_ADDRESS: false,
    ADDRESS_1: '',
    ADDRESS_CITY: '',
    ADDRESS_ZIP: '',
    ADDRESS_STATE: ''
  }

  cardOptions: StripeCardElementOptions = {
    style: {
      base: {
        iconColor: '#666EE8',
        color: '#1C2133',
        fontWeight: 'normal',
        fontFamily: 'Roboto, Open Sans, Segoe UI, sans-serif',
        fontSize: '16px',
        '::placeholder': {
          color: '#CFD7E0'
        }
      }
    }
  };

  elementsOptions: StripeElementsOptions = {
    locale: 'en'
  };

  zipCode: number = null;
  cardNumberValid: boolean = false;
  cardDateValid: boolean = false;
  cardCvvValid: boolean = false;

  isPayVisible: boolean = false;

  @ViewChild('zipInput') zipInput: ElementRef;

  constructor(

    private router: Router,
    private activatedRoute: ActivatedRoute,
    private toastr: ToasterService,
    private reportProvider: ReportProvider,
    private userProvider: UserProvider,
    private stripeService: StripeService,
    public currencyPipe: CurrencyPipe,
    private loaderService: LoaderService,
    private _location: Location,
    private commonProvider: CommonProvider,
    private commonService: CommonService,
  ) { }

  ngOnInit(): void {
    this.subscribeParams = this.activatedRoute.params.subscribe(resRoute => {
      this.pricingPlanType = resRoute.type;
      this.reportId = resRoute.id ? resRoute.id : '';

      if (this.pricingPlanType === PricingPlanType.REPORT_PURCHASE && this.reportId) {
        this.getBasePrice();
      } else if (this.pricingPlanType === PricingPlanType.PROFESSIONAL_PLAN) {
        this.basePrice = PricingConstant.PRICE_PER_YEAR;
        this.payableAmount = this.basePrice;
      }
    });

    this.activatedRoute.queryParams.subscribe(params => {
      if (params?.promo) {
        this.togglePromoCode();
        this.promoCode = params.promo;
        setTimeout(() => {
          this.applyPromoCode();
        }, 500);
      }
    });

    if(this.commonService.oneTimeUserPromoCode){
      this.promoCodeAllowed = true;
      this.promoCode = this.commonService.oneTimeUserPromoCode;
      setTimeout(() => {
        this.applyPromoCode();
      }, 500);
    }
  }

  ngAfterViewInit(): void {
    const eventStream = fromEvent(this.zipInput.nativeElement, 'keyup')
      .pipe(
        map(() => this.checkoutDetails.ADDRESS_ZIP),
        debounceTime(500),
        distinctUntilChanged()
      );

    eventStream.subscribe((input) => {
      this.zipEntered();
    });
  }

  ngOnDestroy(): void {
    if (this.subscribeParams)
      this.subscribeParams.unsubscribe();
  }

  /**
   * @description for go back
   */
  goBack() {
    if (this.reportId && this.pricingPlanType === PricingPlanType.REPORT_PURCHASE) {
      this.router.navigateByUrl(`/report/${this.reportId}/conclusion`);
    } else {
      this._location.back();
    }
  }

  /**
   * @description get base price
   */
  getBasePrice() {
    this.reportProvider.getBillingStatus(this.reportId).subscribe(resp => {
      if (resp.success) {
        const result = resp.result;
        this.basePrice = result.PRICE;
        this.payableAmount = this.basePrice;
        this.isPayVisible = true;
      }
    }, err => {
      // Error Handling
    });
  }

  /**
   * @description pay click
   */
  payClick(): void {
    if (!this.checkoutForm.invalid && this.reportId) {
      if ((this.payableAmount > 0 && this.checkNameLengthValidation()) || (1 == 1)) {
        const loadingObj = {
          loading: true,
          loadingMessage: 'Processing Payment...'
        }
        this.loaderService.isLoading.next(loadingObj);
        this.isPayClicked = true;
        const name = this.checkoutDetails.CARD_NAME;
        if (this.payableAmount > 0) {
          this.reportProvider.purchaseReport(this.reportId, this.promoCodeAllowed ? this.promoCode.trim() : '')
            .pipe(
              switchMap((pi) =>
                this.stripeService.confirmCardPayment(pi?.result?.client_secret, {
                  payment_method: {
                    card: this.card.element,
                    billing_details: {
                      name: name,
                    },
                  },
                })
              )
            )
            .subscribe((result) => {
              this.isPayClicked = false;
              if (result.error) {
                // Show error to your customer (e.g., insufficient funds)
                if(!result.error?.code && result.error['txt']){
                  this.toastr.error(result.error['txt']);
                }else {
                  this.showingErrors(result.error);
                }
                const loadingObj2 = {
                  loading: false,
                  loadingMessage: ''
                }
                this.loaderService.isLoading.next(loadingObj2);
              } else {
                // The payment has been processed!
                if (result.paymentIntent.status === 'succeeded') {
                  // Show a success message to your customer
                  this.checkReportStatus();
                }
              }
            });
        } else {
          this.reportProvider.purchaseReport(this.reportId, this.promoCodeAllowed ? this.promoCode.trim() : '').subscribe((result) => {
            this.isPayClicked = false;
            if (result.success) {
              this.checkReportStatus();
            } else {
              if(!result.error?.code && result.error?.txt){
                this.toastr.error(result.error?.txt);
              }else {
                this.showingErrors(result.error);
              }
              const loadingObj2 = {
                loading: false,
                loadingMessage: ''
              }
              this.loaderService.isLoading.next(loadingObj2);
            }
          });
        }

      } else {
        this.toastr.error('Please enter valid name!');
      }
    } else {
      this.checkoutForm.control.markAllAsTouched();
    }
  }

  /**
   * @description for check report status
   */
  checkReportStatus() {
    this.reportProvider.getBillingStatus(this.reportId).subscribe(resp => {
      if (resp.success) {
        const result = resp.result;
        if (result.STATE == ReportState.UNLOCKED) {
          const loadingObj2 = {
            loading: false,
            loadingMessage: ''
          }
          this.loaderService.isLoading.next(loadingObj2);
          this.router.navigateByUrl(`/report/${this.reportId}/conclusion`);
        } else {
          setTimeout(() => {
            this.checkReportStatus();
          }, 3000);
        }
      } else {
        setTimeout(() => {
          this.checkReportStatus();
        }, 3000);
      }
    }, err => {
      // Error Handling
      setTimeout(() => {
        this.checkReportStatus();
      }, 3000);
    });
  }

  /**
   * @description check box checked for same address of user
   */
  sameAddressChange() {
    if (this.checkoutDetails.SAME_ADDRESS) {
      this.userProvider.getMyProfile().subscribe(res => {
        if (res.success) {
          this.user = res.result;
          this.checkoutDetails.ADDRESS_1 = this.user.ADDRESS;
          this.checkoutDetails.ADDRESS_CITY = this.user.CITY;
          this.checkoutDetails.ADDRESS_ZIP = this.user.ZIP;
          this.checkoutDetails.ADDRESS_STATE = this.user.STATE;
          this.checkoutForm.controls['ADDRESS_1'].disable();
          this.checkoutForm.controls['ADDRESS_ZIP'].disable();
        } else {
          this.toastr.error(res.error.txt);
        }
      }, err => {
        // Error Handling
      });
    } else {
      this.checkoutForm.controls['ADDRESS_1'].enable();
      this.checkoutForm.controls['ADDRESS_ZIP'].enable();
      this.checkoutDetails.ADDRESS_1 = '';
      this.checkoutDetails.ADDRESS_CITY = '';
      this.checkoutDetails.ADDRESS_ZIP = '';
      this.checkoutDetails.ADDRESS_STATE = '';
    }
  }

  /**
   * @description for check name length
   */
  checkNameLengthValidation(): boolean {
    let nameList = this.checkoutDetails.CARD_NAME.split(' ');
    nameList = nameList.filter(item => item);
    if (nameList.length < 2) {
      return false;
    } else {
      return true;
    }
  }

  /**
   * @description for show custom error
   */
  showingErrors(error: any) {
    switch (error?.code) {
      case 'incomplete_number':
      case 'invalid_number':
      case 'incorrect_number':
        this.toastr.error('Invalid Card number!');
        break;
      case 'incomplete_expiry':
      case 'invalid_expiry_month':
      case 'invalid_expiry_year':
        this.toastr.error('Invalid Expiration date!');
        break;
      case 'incomplete_cvc':
      case 'incorrect_cvc':
      case 'invalid_cvc':
        this.toastr.error('Invalid CVC number!');
        break;
      case 'balance_insufficient':
        this.toastr.error('Insufficient balance in account!');
        break;
      case 'card_declined':
        this.toastr.error('Your card was declined!');
        break;
      case 'expired_card':
        this.toastr.error('Your card is expired!');
        break;
      default:
        this.toastr.error('Your card was declined!');
        break;
    }
  }

  /**
   * @description toggle promocode
   */
  togglePromoCode() {
    this.promoCode = '';
    this.isPromoApplied = false;
    this.payableAmount = this.basePrice;
    this.promoCodeAllowed = !this.promoCodeAllowed;
  }

  /**
   * @description apply promocode
   */
  applyPromoCode() {
    if (this.promoCode.trim() && this.reportId) {
      this.reportProvider.applyPromo(this.reportId, this.promoCode.trim()).subscribe(res => {
        if (res.success) {
          this.isPromoApplied = true;
          this.payableAmount = res?.result?.TOTAL_PRICE;
        } else {
          this.promoCode = '';
          this.isPromoApplied = false;
          this.payableAmount = this.basePrice;
          this.toastr.error('Promo Code Invalid!!');
        }
      }, err => {
        this.promoCode = '';
        this.isPromoApplied = false;
        this.payableAmount = this.basePrice;
        this.toastr.error('Promo Code Invalid!!');
      });
    } else {
      this.toastr.error('Please enter valid promo!!')
    }
  }

  /**
   * @description for remove and reset promocode
   */
  removePromoCode() {
    this.promoCode = '';
    this.isPromoApplied = false;
    this.payableAmount = this.basePrice;
  }

  /**
   * @description key press event
   */
  _keyPress(event: any) {
    const pattern = /[0-9]/;
    let inputChar = String.fromCharCode(event.charCode);

    if (!pattern.test(inputChar))
      event.preventDefault();
  }

  /**
   * @description change card details event
   */
  onChangeCardDetals(event: any) {
    if (event.elementType == 'cardNumber') {
      if (event.complete) {
        this.cardNumberValid = true;
      } else {
        this.cardNumberValid = false;
      }
    } else if (event.elementType == 'cardExpiry') {
      if (event.complete) {
        this.cardDateValid = true;
      } else {
        this.cardDateValid = false;
      }
    } else if (event.elementType == 'cardCvc') {
      if (event.complete) {
        this.cardCvvValid = true;
      } else {
        this.cardCvvValid = false;
      }
    }
  }

  /**
   * @description for insert zip
   */
  zipEntered() {
    if (this.checkoutDetails.ADDRESS_ZIP) {
      if (this.checkoutDetails.ADDRESS_ZIP.length === 5) {
        this.commonProvider.identifyZip(this.checkoutDetails.ADDRESS_ZIP).subscribe(resp => {
          if (resp.success) {
            if (resp.result) {
              const zipResult = resp.result;
              this.checkoutDetails.ADDRESS_CITY = zipResult.CITY;
              this.checkoutDetails.ADDRESS_STATE = zipResult.STATE;
            } else {
              this.checkoutDetails.ADDRESS_ZIP = '';
              this.checkoutDetails.ADDRESS_CITY = '';
              this.checkoutDetails.ADDRESS_STATE = '';
            }
          } else {
            this.toastr.error('Invalid Zip Code!');
            this.checkoutDetails.ADDRESS_ZIP = '';
            this.checkoutDetails.ADDRESS_CITY = '';
            this.checkoutDetails.ADDRESS_STATE = '';
          }
        }, err => {
          // Error Handling
        });
      }
    }
  }

}
